import { useCallback, useMemo } from 'react';

import { InputErrorProps } from '@xemplo/input-utils';

import { SelectProps } from './select.types';
import { useSelectStyle } from './use-select-style';

/**
 * Custom hook that generates metadata and props for a select component with chips functionality.
 * This hook handles the composition of various props needed for rendering a select component,
 * including styling, ARIA attributes, event handlers, and accessibility features.
 */
export function useSelectMeta(props: SelectProps) {
  const styles = useSelectStyle(props);
  const onClear = props.chipsProps.onClear;

  const handleClearClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation(); // Prevent popover from opening when clearing
      onClear();
    },
    [onClear]
  );

  return useMemo(() => {
    const { chipsProps, label, name } = props;
    const labelId = label ? `${name}-label` : undefined;

    const errorProps = makeErrorOrDescriptionProps(props);
    const { ariaDescribedBy, hasError } = errorProps;
    const ariaProps = makeSelectAriaProps(props, ariaDescribedBy, labelId, hasError);

    const selectContainerProps = {
      ...ariaProps,
      id: props.triggerId,
      className: styles.container,
      onClick: props.onClick,
      role: 'combobox',
      tabIndex: props.disabled || props.readOnly ? -1 : 0,
    } satisfies React.HTMLAttributes<HTMLDivElement>;

    const labelProps = {
      id: labelId,
      htmlFor: props.name,
      className: styles.label,
    } satisfies React.LabelHTMLAttributes<HTMLLabelElement>;

    const clearIconProps = {
      onClick: handleClearClick,
      'aria-label': 'Clear all chips',
      role: 'button',
    } satisfies React.HTMLAttributes<HTMLSpanElement>;

    return {
      clearIconProps,
      labelProps,
      maybeErrorOrDescriptionProps: errorProps.maybeErrorOrDescriptionProps,
      selectContainerProps,
      chipsProps,
      styles,
      hasError,
    };
  }, [handleClearClick, props, styles]);
}

/**
 * Generates error and description related properties for a select field component.
 *
 * This function extracts relevant properties from the provided `SelectProps` and constructs
 * objects and identifiers used for accessibility and error handling in form fields.
 *
 * @param props - The properties of the select field, including `id`, `name`, `errorMessage`, and `description`.
 * @returns An object containing:
 * - `maybeErrorOrDescriptionProps`: Properties for error and description handling, conforming to `InputErrorProps`.
 * - `ariaDescribedBy`: The ID to be used for the `aria-describedby` attribute, pointing to either the error or description element.
 * - `hasError`: Boolean indicating whether an error is present.
 */
function makeErrorOrDescriptionProps(props: SelectProps) {
  const { id, name, errorMessage, description } = props;

  const fieldId = id ?? name;
  const hasError = !!errorMessage;
  const errorId = hasError ? `${fieldId}-error` : undefined;
  const descriptionId = description ? `${fieldId}-description` : undefined;
  const ariaDescribedBy = hasError ? errorId : descriptionId;

  const maybeErrorOrDescriptionProps = {
    description,
    descriptionProps: descriptionId ? { id: descriptionId } : undefined,
    error: hasError,
    errorMessage,
    errorMessageProps: errorId ? { id: errorId } : undefined,
    testId: fieldId, // This needs to go soon, but some stuff still relies on test id
  } satisfies InputErrorProps;

  return { maybeErrorOrDescriptionProps, ariaDescribedBy, hasError };
}

/**
 * Generates a set of ARIA attributes for a select component based on its props and state.
 *
 * @param props - The properties of the select component, including accessibility and state flags.
 * @param ariaDescribedBy - The ID of the element that describes the select component, if any.
 * @param labelId - The ID of the label element associated with the select component, if any.
 * @param hasError - Indicates whether the select component is in an error state.
 * @returns An object containing ARIA attributes to be applied to the select component.
 */
function makeSelectAriaProps(
  props: SelectProps,
  ariaDescribedBy: string | undefined,
  labelId: string | undefined,
  hasError: boolean
) {
  return {
    'aria-controls': props.open ? props.listboxId : undefined,
    'aria-describedby': ariaDescribedBy,
    'aria-disabled': props.disabled,
    'aria-expanded': props.open,
    'aria-haspopup': 'listbox',
    'aria-invalid': hasError,
    'aria-label': props['aria-label'],
    'aria-labelledby': labelId,
    'aria-readonly': props.readOnly,
    'aria-required': props.required,
  } satisfies React.AriaAttributes;
}
